import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Select,
  Input,
  Alert,
  Empty,
  Spin,
  Button,
  message,
  Modal,
  Tooltip,
  Tag,
  Checkbox
} from 'antd';
import {
  InfoCircleOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  FileTextOutlined
} from '@ant-design/icons';
import { NAMESPACE } from '@/constants';
import { formatMoney } from '@/constants/currency';
import {
  useListIoTemplatesQuery,
  useRaiseIoMutation,
  useListIoSendersQuery,
  useUpdatePrismaStatusMutation,
  usePreviewIoTemplateMutation,
  useGetPlanIoVersionsQuery,
  type IoTemplateColumn,
  type IoPricingSettings,
} from '@/api';
import { classifyPandaDocError, pandaDocErrorMessage } from '@/utils/pandaDoc';
import { TemplateSummary } from './TemplateSummary';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { signOut } from '@/store/auth/actions';
import {
  FooterBar,
  FooterStatus,
  PreviewFrame,
  SplitPane,
  FormPane,
  DocumentPane,
  PreviewPlaceholder,
  PlaceholderTitle,
  PlaceholderHint,
  AlertSpacer,
  SectionLabel,
  SectionDivider,
  NameFieldsRow,
  HalfWidthFormItem,
  LabelWithHint,
  ExistingIoCard,
  ExistingIoText,
  ExistingIoTitle,
  ExistingIoMeta,
  ExistingIoActions
,
  InternalOnlyCard
} from './RaiseIoPanel.styles';

interface RaiseIoFormValues {
  templateId: string;
  name: string;
  recipientEmail: string;
  recipientFirstName?: string;
  recipientLastName?: string;
  countersignerEmail?: string;
  countersignerFirstName?: string;
  countersignerLastName?: string;
  ccEmails?: string[];
  requiresPrisma?: boolean;
  /** Address to send from. Unset sends from whoever raised it. */
  sendAsEmail?: string;
}

interface Props {
  mediaPlanId: string;
  /** Called once the IO has been raised. */
  onDone: () => void;
  onCancel: () => void;
  defaultName?: string;
}

/**
 * A field label with its guidance behind an info icon.
 *
 * The form carries a sentence of explanation per field, which crowds out the
 * fields themselves; moving it into a tooltip keeps the guidance available
 * without making the form read as a document. The icon is focusable so the
 * text is reachable by keyboard, not hover alone.
 */
/**
 * Marks the CC field so autofill can be switched off on its inner input.
 *
 * antd's Select exposes `autoComplete` only on its internal selector, not as a
 * prop, so the attribute has to be set on the rendered input directly. A class
 * rather than an id: Form.Item generates the input's id to pair it with the
 * label, and overriding that would break the association for screen readers.
 */
const CC_FIELD_CLASS = 'io-cc-recipients';

/**
 * Whether a replacement can be issued as an internal amendment.
 *
 * Off until the backend supersedes the previous revision when one is raised:
 * it only does so on a BoldSign signature, which an internal amendment never
 * reaches, so the old revision's bookings were left in place alongside the
 * new ones.
 */
const INTERNAL_AMENDMENT_ENABLED = false;

const labelWithHint = (label: string, hint: string) => (
  <LabelWithHint>
    {label}
    <Tooltip title={hint}>
      <InfoCircleOutlined tabIndex={0} aria-label={hint} />
    </Tooltip>
  </LabelWithHint>
);

/** Raise/sign dates on the existing-IO banner. Falls back to an em dash so a
 *  document with no recorded date still reads as a sentence. */
const formatIoDate = (value?: string): string =>
  value ? new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—';

// A draft can take a few seconds to finish processing (document.uploaded -> document.draft).

// Only Insertion Order templates are eligible. Names like "DO NOT USE IO - ..."
// start with "DO" and are excluded automatically.

export const RaiseIoPanel: FC<Props> = ({ mediaPlanId, onDone, onCancel, defaultName }) => {
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const [form] = Form.useForm<RaiseIoFormValues>();
  const dispatch = useAppDispatch();

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [prepareError, setPrepareError] = useState<string | null>(null);
  const [prepareErrorIsSessionExpired, setPrepareErrorIsSessionExpired] = useState(false);
  // Belt-and-braces re-entrancy guard alongside the button's own loading/disabled state,
  // so a raced double-invocation (e.g. Enter key + click) can never raise two IOs.
  const isSubmittingRef = useRef(false);

  // NRVE's own templates: the document is rendered here, so PandaDoc's template
  // list is no longer what a user picks from.
  const { data, isFetching, isError, error: templatesError } = useListIoTemplatesQuery(undefined, {
  });
  const [raiseIo] = useRaiseIoMutation();
  const { data: senders, isLoading: areSendersLoading } = useListIoSendersQuery();
  const [updatePrismaStatus] = useUpdatePrismaStatusMutation();

  /* What has already been raised against this plan. Drives the warning above
     the form, so an existing IO is visible before anything is filled in. */
  const { data: ioVersions } = useGetPlanIoVersionsQuery(mediaPlanId, { skip: !mediaPlanId });

  /* Applied once the plan's IO state arrives rather than as an initialValue:
     the rule is resolved server-side from the buying point, so it is not known
     when the form first renders. Only sets the field while it is untouched, so
     a late response never overwrites a deliberate change. */
  useEffect(() => {
    if (ioVersions?.requiresClientOrder === undefined) return;
    if (form.isFieldTouched('requiresPrisma')) return;
    form.setFieldValue('requiresPrisma', ioVersions.requiresClientOrder);
  }, [form, ioVersions?.requiresClientOrder]);
  const existingIo = ioVersions?.live;
  const hasLiveIo = Boolean(ioVersions?.hasLiveIo);
  const budgetDrift = ioVersions?.budgetChangedAfterSigning;
  const [replaceExisting, setReplaceExisting] = useState(false);
  /* Raise the revision without sending it to the client. Only meaningful when
     replacing: a first IO is the agreement, so there is always someone to
     sign it. */
  const [internalOnly, setInternalOnly] = useState(false);
  const [previewWithLayout] = usePreviewIoTemplateMutation();

  // Working copy of the template's columns: toggling one re-renders the
  // document without changing the template other IOs use.
  const [columns, setColumns] = useState<IoTemplateColumn[]>([]);
  const [pricing, setPricing] = useState<IoPricingSettings | undefined>();

  const templatesErrorCopy = useMemo(
    () =>
      pandaDocErrorMessage(templatesError, {
        sessionExpired: t('ioSessionExpired'),
        integrationUnavailable: t('ioIntegrationUnavailable'),
        fallback: t('templatesLoadFailed')
      }),
    [templatesError, t]
  );
  const templatesErrorIsSessionExpired = useMemo(
    () => classifyPandaDocError(templatesError) === 'sessionExpired',
    [templatesError]
  );


  // Slugs identify NRVE templates. Only templates switched on in IO Templates
  // are offered, so the dropdown holds the handful actually in use.
  const templateOptions = useMemo(
    () =>
      (data ?? [])
        .filter((template) => template.enabled !== false)
        .map((template) => ({ label: template.name, value: template.slug })),
    [data]
  );

  const revokePreviewUrl = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  };

  const resetState = () => {
    revokePreviewUrl();
    setInternalOnly(false);
    form.resetFields();
    setPreviewUrl(null);
    setPrepareError(null);
    setPrepareErrorIsSessionExpired(false);
    setIsPreparing(false);
    setIsSending(false);
    isSubmittingRef.current = false;
  };

  const handleClose = async () => {
    resetState();
    onCancel();
  };

  // `initialValues` is only read on first render, but the plan that supplies the
  // name loads asynchronously — so seed the field once it arrives, and only when
  // the user has not already typed something.
  useEffect(() => {
    if (defaultName && !form.getFieldValue('name')) {
      form.setFieldValue('name', defaultName);
    }
  }, [defaultName, form]);

  // Open on the default template, the one most IOs use. Same rule as the name:
  // the list loads asynchronously, and a choice already made is left alone.
  useEffect(() => {
    const fallback = (data ?? []).find((template) => template.isDefault && template.enabled !== false);
    if (fallback && !form.getFieldValue('templateId')) {
      form.setFieldValue('templateId', fallback.slug);
    }
  }, [data, form]);

  const selectedTemplate = Form.useWatch('templateId', form);
  const documentName = Form.useWatch('name', form);
  const recipientEmail = Form.useWatch('recipientEmail', form);

  // Raising is irreversible from the client's side, so the action stays
  // disabled until every required field holds a usable value — not merely
  // touched, which antd's own validation state would allow.
  const isFormComplete = Boolean(
    selectedTemplate &&
      documentName?.trim() &&
      recipientEmail?.trim() &&
      /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipientEmail.trim())
  );

  /**
   * Stops the browser offering saved addresses in the CC field.
   *
   * This is a tags input: a suggestion accepted by accident becomes a real CC
   * recipient on a contract going to a client, added as a tag rather than left
   * as typed text, so it is easy to miss before sending. The operator's own
   * address is exactly what a browser would offer, and exactly what should not
   * be copied in silently.
   *
   * Set on the DOM node because antd does not pass `autoComplete` through to
   * the selector's input. `new-password` rather than `off`: Chrome ignores
   * `off` on inputs it reads as address-shaped, but honours a value it treats
   * as a fresh credential. The other two attributes cover Safari and older
   * Firefox, which use their own vocabulary.
   */
  useEffect(() => {
    const apply = () => {
      const input = document.querySelector<HTMLInputElement>(
        `.${CC_FIELD_CLASS} input`
      );
      if (!input) return false;
      input.setAttribute('autocomplete', 'new-password');
      input.setAttribute('autocorrect', 'off');
      input.setAttribute('spellcheck', 'false');
      return true;
    };

    if (apply()) return undefined;

    /* The field renders behind a loading state, so on the first pass the input
       may not exist yet. Watching the subtree applies the attributes the moment
       it appears, rather than leaving autofill live because one render was
       early. */
    const observer = new MutationObserver(() => {
      if (apply()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const chosen = (data ?? []).find((item) => item.slug === selectedTemplate);
    setColumns(chosen ? chosen.columns.map((column) => ({ ...column })) : []);
    setPricing(chosen?.pricing ? { ...chosen.pricing } : undefined);
  }, [data, selectedTemplate]);

  // Render as soon as a template is chosen, so opening Raise IO shows the
  // document the client will receive rather than an empty form.
  useEffect(() => {
    if (!selectedTemplate) return undefined;
    let objectUrl: string | null = null;
    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        const html = await previewWithLayout({
          slug: selectedTemplate,
          columns,
          pricing,
          mediaPlanId
        }).unwrap();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
        setPreviewUrl(objectUrl);
      } catch {
        if (!cancelled) setPreviewUrl(null);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedTemplate, columns, pricing, mediaPlanId, previewWithLayout]);

  /* Replacing a signed IO retires its bookings once the new one is signed, so
     it is confirmed explicitly rather than on a single click. */
  const confirmReplacement = () =>
    new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: t('ioReplaceConfirmTitle'),
        /* An internal amendment is never signed, so the standard wording —
           "stays in force until the new one is signed" — would describe
           something that is not going to happen. */
        content: internalOnly ? t('ioInternalConfirmBody') : t('ioReplaceConfirmBody'),
        okText: t('ioReplaceConfirmOk'),
        cancelText: t('cancel', { ns: NAMESPACE.COMMON }),
        onOk: () => resolve(true),
        onCancel: () => resolve(false)
      });
    });

  const handleRaise = async () => {
    // Guard against a raced double-invocation so the "Raise IO" button can never fire
    // sendDocument twice and produce duplicate IOs.
    if (isSubmittingRef.current) return;

    if (hasLiveIo && existingIo?.signed && !(await confirmReplacement())) return;

    const values = form.getFieldsValue();
    isSubmittingRef.current = true;
    setIsSending(true);
    try {
      // The document is created here rather than at preview time, so backing out
      // of the flow leaves nothing behind in PandaDoc.
      await raiseIo({
        mediaPlanId,
        templateId: values.templateId,
        // Send the layout as reviewed, so the signed document matches the
        // preview rather than reverting to the stored template.
        columns,
        pricing,
        name: values.name.trim(),
        recipient: {
          email: values.recipientEmail.trim(),
          first_name: values.recipientFirstName?.trim() || undefined,
          last_name: values.recipientLastName?.trim() || undefined
        },
        // Optional: when a Skyrise signatory is given the document is
        // counter-signed, so it gains a second signature block.
        ...(values.countersignerEmail?.trim() && {
          countersigner: {
            email: values.countersignerEmail.trim(),
            /* Joined here so the provider receives one display name, but
               captured as two fields: a signature block showing only a first
               name reads as unfinished on a contract. */
            name:
              [values.countersignerFirstName, values.countersignerLastName]
                .map((part) => part?.trim())
                .filter(Boolean)
                .join(' ') || undefined
          }
        }),
        /* Omitted entirely when empty, so the backend applies only its own
           standing list rather than merging against an empty array. */
        /* Sent only when one was chosen: unset means the raiser's own address,
           which the backend already resolves. */
        ...(values.sendAsEmail?.trim() && { sendAsEmail: values.sendAsEmail.trim() }),
        ...(values.ccEmails?.length && {
          ccEmails: values.ccEmails.map((email) => email.trim()).filter(Boolean)
        }),
        /* Only sent when an IO is actually live: the backend treats it as an
           explicit instruction to supersede, not a default. */
        ...(hasLiveIo && { replaceExisting: true }),
        /* Only sent when chosen. The backend defaults to sending, so an unset
           flag can never silently withhold an IO the client should see. */
        ...(internalOnly && { internalOnly: true }),
        // Decides whether this raise goes for signature at all, so it has to
        // travel with the raise rather than only be recorded after it.
        ...(form.isFieldTouched('requiresPrisma') && { requiresPrisma: Boolean(values.requiresPrisma) })
      }).unwrap();

      /* Recorded after the raise rather than as part of it: the IO is already
         out for signature, and a plan that fails to record its Prisma
         requirement should surface that rather than imply the raise failed.
         Bookings are withheld from the booking sheet until the Prisma order is
         confirmed, so getting this on the plan before signature matters. */
      /* Sent whenever the user changed it, in either direction. The box can
         now arrive pre-ticked from the buying point, so unticking it is a
         deliberate override of the rule — and only sending the true case would
         silently ignore that. */
      if (form.isFieldTouched('requiresPrisma')) {
        try {
          await updatePrismaStatus({
            mediaPlanId,
            requiresPrisma: Boolean(values.requiresPrisma)
          }).unwrap();
        } catch {
          message.warning(t('ioPrismaNotRecorded'));
        }
      }

      message.success(
        /* Says explicitly that nothing went to the client. "Raised" alone
           would read the same as a document that had just been emailed out,
           which is the one thing the user needs to know did not happen. */
        internalOnly
          ? t('ioInternalRaisedSuccess', { version: ioVersions?.nextVersion ?? '' })
          : hasLiveIo
            ? t('ioRaisedReplacementSuccess', { version: ioVersions?.nextVersion ?? '' })
            : t('ioRaisedSuccessfully')
      );
      resetState();
      onDone();
    } catch (err) {
      /* The backend refuses a duplicate with 409 rather than raising a second
         document. Surfaced as its own message: the generic copy would read as
         a failure when nothing went wrong. */
      if ((err as { status?: number })?.status === 409) {
        message.warning(t('ioConflictNeedsReplace'));
        return;
      }

      const kind = classifyPandaDocError(err);
      const text = pandaDocErrorMessage(err, {
        sessionExpired: t('ioSessionExpired'),
        integrationUnavailable: t('ioIntegrationUnavailable'),
        fallback: t('ioRaiseFailed')
      });
      if (kind === 'sessionExpired') {
        message.error({
          content: text,
          key: 'raise-io-session-expired'
        });
      } else {
        message.error(text);
      }
    } finally {
      setIsSending(false);
      isSubmittingRef.current = false;
    }
  };

  /* What is still missing, in the order the form asks for it. Named beside
     the send button so a disabled action always says why it is disabled. */
  const blockers: string[] = [];
  if (!selectedTemplate) blockers.push(t('template'));
  if (!documentName?.trim()) blockers.push(t('documentName'));
  if (!recipientEmail?.trim() || !isFormComplete) blockers.push(t('recipientEmail'));
  const needsReplaceChoice = hasLiveIo && !replaceExisting;

  const footer = (
    <FooterBar>
      <FooterStatus>
        {needsReplaceChoice
          ? t('ioNeedsReplaceChoice')
          : blockers.length
            ? t('ioStillNeeded', { fields: blockers.join(', ') })
            : t('ioReadyToSend')}
      </FooterStatus>
      <Button onClick={handleClose} disabled={isSending}>
        {t('cancel', { ns: NAMESPACE.COMMON })}
      </Button>
      <Button
        type="primary"
        loading={isSending}
        disabled={
          isSending || isError || isFetching || !isFormComplete || (hasLiveIo && !replaceExisting)
        }
        onClick={handleRaise}
      >
        {t('raiseIo')}
      </Button>
    </FooterBar>
  );

  const signInAgain = () => {
    dispatch(signOut());
  };

  return (
    <>
      {isError ? (
        <Alert
          type="error"
          role="alert"
          message={templatesErrorCopy}
          showIcon
          action={
            templatesErrorIsSessionExpired ? (
              <Button size="small" type="link" onClick={signInAgain}>
                {t('ioSignInAgain')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <SplitPane>
          <FormPane>
        <Spin spinning={isPreparing} tip={t('ioPreparingPreview')} aria-live="polite" aria-busy={isPreparing}>
          {/* An IO already exists. Shown as a compact record of what is there —
              version, state, date — with replacing it as one explicit action,
              rather than a paragraph the user has to read to find a checkbox. */}
          {/* Above the existing-IO card on purpose: that card says what is
              already there, this says it no longer matches the deal, and the
              second is the reason someone is on this screen. Only shown with a
              live IO — once a replacement is raised the flag is cleared, and
              there is nothing left to disagree with. */}
          {budgetDrift && hasLiveIo && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('ioBudgetChangedTitle')}
              description={t('ioBudgetChangedBody', {
                /* The plan's own currency, taken from the live IO rather than
                   assumed: these figures are money and showing them under the
                   wrong symbol would be worse than showing them bare. */
                signed: formatMoney(budgetDrift.signedBudget, budgetDrift.currency),
                current: formatMoney(budgetDrift.currentBudget, budgetDrift.currency)
              })}
            />
          )}

          {hasLiveIo && existingIo && (
            <ExistingIoCard $signed={Boolean(existingIo.signed)}>
              {existingIo.signed ? <CheckCircleFilled /> : <ClockCircleFilled />}
              <ExistingIoText>
                <ExistingIoTitle>
                  <span>{t('ioVersionLabel', { version: existingIo.version })}</span>
                  <Tag color={existingIo.signed ? 'gold' : 'blue'} style={{ marginInlineEnd: 0 }}>
                    {existingIo.signed ? t('ioStateSigned') : t('ioStateAwaitingSignature')}
                  </Tag>
                </ExistingIoTitle>
                <ExistingIoMeta>
                  {existingIo.signed
                    ? t('ioSignedOn', {
                        date: formatIoDate(existingIo.completedAt ?? existingIo.raisedAt)
                      })
                    : t('ioRaisedOn', { date: formatIoDate(existingIo.raisedAt) })}
                </ExistingIoMeta>
                {/* What raising again actually does, said here rather than in
                    a tooltip on the button that does it. */}
                <ExistingIoMeta>
                  {existingIo.signed ? t('ioReplaceHintSigned') : t('ioReplaceHintUnsigned')}
                </ExistingIoMeta>
              </ExistingIoText>
              {replaceExisting ? (
                <ExistingIoActions>
                  <Tag color="green" style={{ marginInlineEnd: 0 }}>
                    {t('ioWillBeReplaced')}
                  </Tag>
                  <Button
                    size="small"
                    type="text"
                    onClick={() => {
                      setReplaceExisting(false);
                      /* Cleared with it: the choice only exists while
                         replacing, and leaving it set would apply silently if
                         replacing were chosen again. */
                      setInternalOnly(false);
                    }}
                  >
                    {t('ioReplaceUndo')}
                  </Button>
                </ExistingIoActions>
              ) : (
                <Button
                  size="small"
                  /* Danger styling on a signed IO: replacing one supersedes a
                     contract the client has already put their name to, which
                     a default-styled button did not say. The confirmation
                     itself fires at Raise, where the act becomes irreversible. */
                  danger={Boolean(existingIo.signed)}
                  onClick={() => setReplaceExisting(true)}
                >
                  {t('ioReplaceExisting')}
                </Button>
              )}
            </ExistingIoCard>
          )}

          {/* Offered only once replacing is chosen, and never for a campaign
              that books on a client order — those are already not sent, so the
              choice would be meaningless. */}
          {/* Hidden until the backend retires the previous revision's
              bookings on an internal amendment: today nothing supersedes it,
              so both revisions' bookings stay and the budget counts twice. */}
          {INTERNAL_AMENDMENT_ENABLED && replaceExisting && !ioVersions?.requiresClientOrder && (
            <InternalOnlyCard>
              <Checkbox
                checked={internalOnly}
                onChange={(event) => setInternalOnly(event.target.checked)}
              >
                {t('ioInternalOnly')}
              </Checkbox>
              <ExistingIoMeta>{t('ioInternalOnlyHint')}</ExistingIoMeta>
            </InternalOnlyCard>
          )}
          {prepareError && (
            <AlertSpacer>
              <Alert
                type="error"
                role="alert"
                message={prepareError}
                showIcon
                action={
                  prepareErrorIsSessionExpired ? (
                    <Button size="small" type="link" onClick={signInAgain}>
                      {t('ioSignInAgain')}
                    </Button>
                  ) : undefined
                }
              />
            </AlertSpacer>
          )}
          <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            initialValues={{ name: defaultName ?? '' }}
            preserve={false}
          >
            <SectionLabel type="secondary">{t('ioDocumentSection')}</SectionLabel>
            <SectionDivider />

            <Form.Item
              name="templateId"
              label={t('template')}
              rules={[{ required: true, message: t('templateRequired') }]}
            >
              <Select
                id="raiseIoTemplateId"
                placeholder={t('selectTemplate')}
                options={templateOptions}
                loading={isFetching}
                showSearch
                optionFilterProp="label"
                /* antd's default 256px shows about five options, so the rest of
                   the shipped templates sat below the fold behind a scrollbar
                   that is easy to miss — and a list of nine read as a list of
                   five. Tall enough for the full set, capped against the
                   viewport so a longer list still scrolls rather than running
                   off the screen. */
                listHeight={Math.min(templateOptions.length * 34 + 12, 420)}
                getPopupContainer={() => document.body}
                notFoundContent={
                  isFetching ? (
                    <Spin size="small" />
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('ioNoTemplates')} />
                  )
                }
              />
            </Form.Item>
            {!isFetching && templateOptions.length === 0 && (
              <AlertSpacer $pullUp>
                <Alert type="warning" role="status" message={t('ioTemplatesEmpty')} showIcon />
              </AlertSpacer>
            )}

            <Form.Item
              name="name"
              label={t('documentName')}
              extra={t('documentNameHint')}
              rules={[{ required: true, message: t('documentNameRequired') }]}
            >
              <Input placeholder={t('documentName')} />
            </Form.Item>


            <SectionDivider />
            <SectionLabel type="secondary">{t('ioRecipientSection')}</SectionLabel>

            <Form.Item
              name="recipientEmail"
              label={t('recipientEmail')}
              rules={[
                { required: true, message: t('recipientEmailRequired') },
                { type: 'email', message: t('recipientEmailInvalid') }
              ]}
            >
              <Input placeholder="name@example.com" />
            </Form.Item>

            <NameFieldsRow>
              <HalfWidthFormItem
                name="recipientFirstName"
                label={t('recipientFirstName')}
              >
                <Input placeholder={t('recipientNamePlaceholder')} />
              </HalfWidthFormItem>
              <HalfWidthFormItem name="recipientLastName" label={t('recipientLastName')}>
                <Input />
              </HalfWidthFormItem>
            </NameFieldsRow>

            <SectionDivider />
            <SectionLabel type="secondary">{t('ioCountersignerSection')}</SectionLabel>

            <Form.Item
              name="countersignerEmail"
              label={labelWithHint(t('countersignerEmail'), t('countersignerHint'))}
              rules={[{ type: 'email', message: t('recipientEmailInvalid') }]}
            >
              <Input placeholder="name@skyri.se" />
            </Form.Item>

            <NameFieldsRow>
              <HalfWidthFormItem
                name="countersignerFirstName"
                label={t('countersignerFirstName')}
              >
                <Input placeholder={t('recipientNamePlaceholder')} />
              </HalfWidthFormItem>
              <HalfWidthFormItem
                name="countersignerLastName"
                label={t('countersignerLastName')}
              >
                <Input />
              </HalfWidthFormItem>
            </NameFieldsRow>

            <SectionDivider />
            <SectionLabel type="secondary">{t('ioPrismaSection')}</SectionLabel>

            {/* Ticking this withholds the campaign's bookings from the booking
                sheet until someone confirms the Prisma order has come back, so
                it is a billing control rather than a note. */}
            {/* No initialValue: the answer comes from the buying point, applied
                by the effect below once the plan's IO state has loaded.
                Hardcoding false meant a configured buying point was ignored
                and the box always started clear. */}
            <Form.Item name="requiresPrisma" valuePropName="checked">
              <Checkbox>{labelWithHint(t('ioRequiresPrisma'), t('ioRequiresPrismaHint'))}</Checkbox>
            </Form.Item>

            <SectionDivider />
            <SectionLabel type="secondary">{t('ioSenderSection')}</SectionLabel>

            {/* Defaults to whoever is raising it. Offered because an IO is
                often raised by someone other than the account manager the
                client deals with — cover, or an admin raising on their behalf —
                and the client should hear from the person they know.

                Only addresses the provider will send as are listed: a pending
                identity would be ignored and the document would go from the
                account instead, with nothing saying so. */}
            <Form.Item
              name="sendAsEmail"
              label={labelWithHint(t('ioSendAs'), t('ioSendAsHint'))}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                loading={areSendersLoading}
                placeholder={t('ioSendAsPlaceholder')}
                options={(senders?.senders ?? []).map((sender) => ({
                  label: sender.name === sender.email ? sender.email : `${sender.name} (${sender.email})`,
                  value: sender.email
                }))}
              />
            </Form.Item>

            <SectionDivider />
            <SectionLabel type="secondary">{t('ioCcSection')}</SectionLabel>

            <Form.Item
              name="ccEmails"
              label={labelWithHint(t('ioCcEmails'), t('ioCcHint'))}
              /* Each address is validated as it is added rather than on submit,
                 so a typo is caught while the person is still looking at it. */
              rules={[
                {
                  validator: (_, value: string[] | undefined) => {
                    const invalid = (value ?? []).filter(
                      (email) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())
                    );
                    return invalid.length
                      ? Promise.reject(new Error(t('ioCcInvalid', { emails: invalid.join(', ') })))
                      : Promise.resolve();
                  }
                }
              ]}
            >
              {/* Autofill is suppressed deliberately. This is a tags field, so a
                  browser or password manager offering the operator's own saved
                  address turns one careless keystroke into a real CC recipient
                  on a contract going to a client — the address is added as a
                  tag, not merely typed, so it is easy to miss before sending.
                  `new-password` rather than `off` because Chrome ignores `off`
                  on address-shaped inputs; the random name keeps the field from
                  matching anything the browser has previously saved. */}
              <Select
                mode="tags"
                tokenSeparators={[',', ' ', ';']}
                open={false}
                suffixIcon={null}
                placeholder={t('ioCcPlaceholder')}
                className={CC_FIELD_CLASS}
              />
            </Form.Item>

          </Form>
        </Spin>
            {/* Only once a template is chosen: an unselected `templateId` would
                otherwise match a record with no slug and render an empty card. */}
            <TemplateSummary
              template={
                selectedTemplate
                  ? (data ?? []).find((item) => item.slug === selectedTemplate)
                  : undefined
              }
              columns={columns}
              onColumnsChange={setColumns}
              pricing={pricing}
              onPricingChange={setPricing}
            />
          </FormPane>
          <DocumentPane $hasDocument={Boolean(previewUrl)}>
            {previewUrl ? (
              /* Sandboxed: the HTML comes from the server and a blob: URL
                 inherits this app's origin, so unsandboxed it could script
                 the page and reach the signed-in session. The document is
                 static markup and needs none of what the sandbox withholds. */
              <PreviewFrame title={t('raiseIo')} src={previewUrl} sandbox="" />
            ) : (
              /* Two different states that looked identical before: nothing
                 chosen yet, and a chosen template still rendering. */
              <PreviewPlaceholder>
                {isPreparing || selectedTemplate ? (
                  <>
                    <Spin />
                    <PlaceholderTitle>{t('ioPreviewLoading')}</PlaceholderTitle>
                  </>
                ) : (
                  <>
                    <FileTextOutlined />
                    <PlaceholderTitle>{t('ioPreviewEmptyTitle')}</PlaceholderTitle>
                    <PlaceholderHint>{t('ioPreviewEmptyHint')}</PlaceholderHint>
                  </>
                )}
              </PreviewPlaceholder>
            )}
          </DocumentPane>
        </SplitPane>
      )}
      {footer}
    </>
  );
};

export default RaiseIoPanel;
